import {Inject, Injectable, Optional} from '@angular/core';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {EReturnImageResizeType} from '@shared-models/common.enums';
import {CommonConfig} from '@ui-app/configs/common.config';
import {PhotoUploadService} from '@ui-app/components/camera/camera/photo-upload.service';
import {DOCUMENT} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {API_CONFIG, IDownloadUrlResponse} from '@shared-models/common.models';
import {switchMap} from 'rxjs/operators';

let ApiConfig;
@Injectable()
export class ImageService {
    readonly imageDefaultType = CommonConfig.cameraConfig.defaultImageType.split('/')[1];

    constructor(
        private httpClient: HttpClient,
        @Optional() @Inject(API_CONFIG) public apiConfig: string,
        @Optional() @Inject(PhotoUploadService) private photoUploadService: PhotoUploadService,
        @Inject(DOCUMENT) private doc: Document,
    ) {
        ApiConfig = apiConfig;
    }

    changeImageSize(
            file: File,
            type: string = this.imageDefaultType,
            maxSize: number,
            returnType: string = EReturnImageResizeType.blob,
            newFileName: string): Observable<any> {
        const returnDataUrl$ = new Subject();
        const reader    = new FileReader();
        const image     = new Image();
        if (!file.type.match(/image.*/)) {
            return;
        }
        reader.onload = (readerEvent: ProgressEvent<FileReader>) => {
            image.onload = () => {
                returnDataUrl$.next(this.resize(image, type, maxSize, returnType, newFileName));
            };
            image.src = readerEvent.target.result as string;
        };
        reader.readAsDataURL(file);
        return returnDataUrl$;
    }

    resize(
        image: HTMLImageElement,
        type: string = this.imageDefaultType,
        maxSize: number,
        returnType: string = EReturnImageResizeType.blob,
        newFileName: string): string {

        const canvas    = this.doc.createElement('canvas');
        let width       = image.width;
        let height      = image.height;
        let output;

        if (width > height) {
            if (width > maxSize) {
                height *= maxSize / width;
                width = maxSize;
            }
        } else {
            if (height > maxSize) {
                width *= maxSize / height;
                height = maxSize;
            }
        }

        canvas.width    = width;
        canvas.height   = height;

        canvas.getContext('2d').drawImage(image, 0, 0, width, height);

        let dataUrl   = canvas.toDataURL('image/jpeg');
        dataUrl       = this.photoUploadService.recursiveCompress(canvas, dataUrl, 0.9);

        switch (returnType) {
            case EReturnImageResizeType.blob: {
                output = this.toBlob(dataUrl, this.imageDefaultType);
                break;
            }
            case EReturnImageResizeType.dataUrl: {
                output = dataUrl;
                break;
            }
            case EReturnImageResizeType.file: {
                output = this.toFIle(this.toBlob(dataUrl), this.imageDefaultType, newFileName);
                break;
            }
        }
        return output;
    }

    toBlob(dataUrl: string, format: string = 'jpg'): Blob {
        const blobBin = atob(dataUrl.split(',')[1]);
        const array = [];
        for (let i = 0; i < blobBin.length; i++) {
            array.push(blobBin.charCodeAt(i));
        }
        return new Blob([new Uint8Array(array)], { type: `image/${format}` });
    }

    toFIle(blob: Blob, format: string = 'jpg', newFileName: string): File {
        return new File(
            [blob],
            `${newFileName}.${format}`,
            { type: `image/${format}` }
        );
    }
    blobToImage(blob: Blob): ReplaySubject<HTMLImageElement> {
        if (!blob) {
            return;
        }
        const result$ = new ReplaySubject(1);
        const fileReader = new FileReader();

        fileReader.onload = () => {
            const image = new Image();
            image.onload = () => {
                result$.next(image);
                result$.complete();
            };
            if (typeof fileReader.result === 'string') {
                image.src = fileReader.result;
            }
        };

        fileReader.readAsDataURL(blob);
        return result$ as ReplaySubject<HTMLImageElement>;

    }

    fromDataUrlToFile(dataUrl: string, fileName: string, imageType: string = this.imageDefaultType): File {
        return this.toFIle(this.toBlob(dataUrl), imageType, fileName);
    }

    getBlobImageByUId(uId: string): Observable<Blob> {
        return this.httpClient.get(ApiConfig.File.fileUrl + `/${uId}`, { responseType: 'blob' });
    }

    getBlobImageByUidFromS3(uId: string, isVisitor: boolean = false): Observable<Blob> {
        const url = isVisitor ? ApiConfig.File.getVisitorDownloadUrl : ApiConfig.File.getDownloadUrl;
        return this.httpClient.get(url.replace('{uId}', uId))
            .pipe(switchMap((value: IDownloadUrlResponse) => {
                return this.httpClient.get(value.url, { responseType: 'blob' });
            }));
    }

    changeFileName(file: File, fileName: string): File {
        const fileType = file.name.substr(file.name.lastIndexOf('.'));

        if (fileName) {
            return new File([file], `${fileName}${fileType}`, { type: file.type });
        } else {
            return new File([file], `${file.name}`, { type: file.type });
        }
    }
}
